// Configuración del taller. Taller-Game → Configuración.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, ExternalLink, PackageSearch, RefreshCw, Save, Settings2, Tv } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, ConfigTaller as ConfigTallerData, tallerRpc, QK, useDialogoEnLinea } from './shared';

interface OpcionesF4 { categorias: string[]; centros: { id: string; nombre: string }[]; cc_modo: string }
interface FormF4 { bodegaje_diario: string; producto_bodegaje_id: string; avisos_dias: string; categoria_usados: string; centro_costo_usados: string; garantia_usados_dias: string }
const VACIO_F4: FormF4 = { bodegaje_diario: '1', producto_bodegaje_id: '', avisos_dias: '30, 60, 90', categoria_usados: '', centro_costo_usados: '', garantia_usados_dias: '30' };
/** Columnas de la fase 4 (957): config_get() ya las trae (to_jsonb del registro completo), pero el tipo compartido no las declara. */
type ConfigF4 = NonNullable<ConfigTallerData['config']> & {
  bodegaje_diario: number; producto_bodegaje_id: string | null; avisos_dias: number[]; categoria_usados: string | null; centro_costo_usados: string | null; garantia_usados_dias: number;
};

const urlTvTaller = (token: string) => `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/taller-tv/${token}`;

interface Form {
  nombre_comercial: string;
  caja_id: string;
  producto_diagnostico_id: string;
  producto_mano_obra_id: string;
  forma_anticipo_id: string;
  tarifa_diagnostico: string;
  anticipo_pct: string;
  garantia_dias: string;
  abandono_dias: string;
  bodegaje_desde_dias: string;
  terminos: string;
}

const VACIO: Form = {
  nombre_comercial: '', caja_id: '', producto_diagnostico_id: '', producto_mano_obra_id: '', forma_anticipo_id: '',
  tarifa_diagnostico: '15', anticipo_pct: '50', garantia_dias: '90', abandono_dias: '90', bodegaje_desde_dias: '30', terminos: '',
};

export function ConfigTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isLoading } = useQuery<ConfigTallerData>({ queryKey: [QK, 'config'], queryFn: () => tallerRpc<ConfigTallerData>('config_get') });
  const { data: opcF4 } = useQuery<OpcionesF4>({ queryKey: [QK, 'config_f4_opciones'], queryFn: () => tallerRpc<OpcionesF4>('config_f4_opciones') });
  const [form, setForm] = useState<Form>(VACIO);
  const [formF4, setFormF4] = useState<FormF4>(VACIO_F4);
  const [copiado, setCopiado] = useState(false);

  const { data: tv, isError: tvError } = useQuery<{ token: string }>({
    queryKey: [QK, 'tv_token'], queryFn: () => tallerRpc<{ token: string }>('tv_token'), retry: false,
  });

  const regenerarTv = useMutation({
    mutationFn: () => tallerRpc<{ token: string }>('tv_token_regenerar'),
    onSuccess: () => { toast.success('Enlace de la TV regenerado: el anterior dejó de funcionar'); void qc.invalidateQueries({ queryKey: [QK, 'tv_token'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const copiarUrlTv = async (url: string) => {
    try { await navigator.clipboard.writeText(url); setCopiado(true); setTimeout(() => setCopiado(false), 1500); }
    catch { toast.error('No se pudo copiar'); }
  };

  useEffect(() => {
    const c = data?.config;
    if (!c) return;
    setForm({
      nombre_comercial: c.nombre_comercial ?? '',
      caja_id: c.caja_id ?? '',
      producto_diagnostico_id: c.producto_diagnostico_id ?? '',
      producto_mano_obra_id: c.producto_mano_obra_id ?? '',
      forma_anticipo_id: c.forma_anticipo_id ?? '',
      tarifa_diagnostico: String(c.tarifa_diagnostico ?? 15),
      anticipo_pct: String(c.anticipo_pct ?? 50),
      garantia_dias: String(c.garantia_dias ?? 90),
      abandono_dias: String(c.abandono_dias ?? 90),
      bodegaje_desde_dias: String(c.bodegaje_desde_dias ?? 30),
      terminos: c.terminos ?? '',
    });
    const c4 = c as ConfigF4;
    setFormF4({
      bodegaje_diario: String(c4.bodegaje_diario ?? 1),
      producto_bodegaje_id: c4.producto_bodegaje_id ?? '',
      avisos_dias: (c4.avisos_dias ?? [30, 60, 90]).join(', '),
      categoria_usados: c4.categoria_usados ?? '',
      centro_costo_usados: c4.centro_costo_usados ?? '',
      garantia_usados_dias: String(c4.garantia_usados_dias ?? 30),
    });
  }, [data]);

  const guardar = useMutation({
    mutationFn: () => tallerRpc('config_guardar', {
      _d: {
        nombre_comercial: form.nombre_comercial.trim(),
        caja_id: form.caja_id || null,
        producto_diagnostico_id: form.producto_diagnostico_id || null,
        producto_mano_obra_id: form.producto_mano_obra_id || null,
        forma_anticipo_id: form.forma_anticipo_id || null,
        tarifa_diagnostico: Number(form.tarifa_diagnostico) || 0,
        anticipo_pct: Number(form.anticipo_pct) || 0,
        garantia_dias: Number(form.garantia_dias) || 0,
        abandono_dias: Number(form.abandono_dias) || 0,
        bodegaje_desde_dias: Number(form.bodegaje_desde_dias) || 0,
        terminos: form.terminos.trim() || null,
      },
    }),
    onSuccess: () => { toast.success('Configuración guardada'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardarF4 = useMutation({
    mutationFn: () => tallerRpc('config_f4_guardar', {
      _d: {
        bodegaje_diario: Number(formF4.bodegaje_diario) || 0,
        producto_bodegaje_id: formF4.producto_bodegaje_id || null,
        avisos_dias: formF4.avisos_dias.split(',').map((x) => Number(x.trim())).filter((x) => Number.isFinite(x) && x > 0),
        categoria_usados: formF4.categoria_usados || null,
        centro_costo_usados: formF4.centro_costo_usados || null,
        garantia_usados_dias: Number(formF4.garantia_usados_dias) || 0,
      },
    }),
    onSuccess: () => { toast.success('Configuración de la fase 4 guardada'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const faltantes: string[] = [];
  if (!form.caja_id) faltantes.push('Caja del POS del taller');
  if (!form.producto_diagnostico_id) faltantes.push('Producto/servicio de diagnóstico');
  if (!form.producto_mano_obra_id) faltantes.push('Producto/servicio de mano de obra');
  if (!form.forma_anticipo_id) faltantes.push('Forma de pago «Anticipo de cliente»');

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Settings2 className="w-5 h-5" /> Configuración del taller</h1>

      {faltantes.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-800">
          <CardContent className="flex items-start gap-2 p-3 text-sm text-amber-800 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Falta configurar antes de operar:</p>
              <ul className="list-disc pl-5">{faltantes.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Datos generales</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Nombre comercial">
            <input className={fieldCls} value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} placeholder="Ej. Taller Game Center" />
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Caja y facturación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Caja del POS del taller *">
            <select className={fieldCls} value={form.caja_id} onChange={(e) => setForm({ ...form, caja_id: e.target.value })}>
              <option value="">— Elegir —</option>
              {data?.cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">La entrega del equipo se factura en esta caja (factura SRI real); requiere su sesión de caja abierta para poder entregar.</p>

          <Campo label="Producto/servicio de diagnóstico *">
            <select className={fieldCls} value={form.producto_diagnostico_id} onChange={(e) => setForm({ ...form, producto_diagnostico_id: e.target.value })}>
              <option value="">— Elegir —</option>
              {data?.servicios.map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Producto/servicio de mano de obra *">
            <select className={fieldCls} value={form.producto_mano_obra_id} onChange={(e) => setForm({ ...form, producto_mano_obra_id: e.target.value })}>
              <option value="">— Elegir —</option>
              {data?.servicios.map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.nombre}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">Ambos deben ser servicios del inventario (no controlan stock): se usan para facturar el diagnóstico y la mano de obra al entregar el equipo.</p>

          <Campo label="Forma de pago «Anticipo de cliente» *">
            <select className={fieldCls} value={form.forma_anticipo_id} onChange={(e) => setForm({ ...form, forma_anticipo_id: e.target.value })}>
              <option value="">— Elegir —</option>
              {data?.formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">Debe estar ligada a la cuenta contable de anticipos de clientes: así el cierre de caja cancela el pasivo cuando el anticipo se aplica a la entrega.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Tarifas y plazos</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Campo label="Tarifa de diagnóstico sin IVA ($)">
            <input type="number" min="0" step="0.01" className={fieldCls} value={form.tarifa_diagnostico} onChange={(e) => setForm({ ...form, tarifa_diagnostico: e.target.value })} />
          </Campo>
          <Campo label="% de anticipo sugerido">
            <input type="number" min="0" max="100" className={fieldCls} value={form.anticipo_pct} onChange={(e) => setForm({ ...form, anticipo_pct: e.target.value })} />
          </Campo>
          <Campo label="Días de garantía por defecto">
            <input type="number" min="0" className={fieldCls} value={form.garantia_dias} onChange={(e) => setForm({ ...form, garantia_dias: e.target.value })} />
          </Campo>
          <Campo label="Días para considerar abandonado">
            <input type="number" min="0" className={fieldCls} value={form.abandono_dias} onChange={(e) => setForm({ ...form, abandono_dias: e.target.value })} />
          </Campo>
          <Campo label="Bodegaje desde el día" className="col-span-2">
            <input type="number" min="0" className={fieldCls} value={form.bodegaje_desde_dias} onChange={(e) => setForm({ ...form, bodegaje_desde_dias: e.target.value })} />
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Condiciones</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Campo label="Condiciones impresas en la orden y el portal">
            <textarea className={`${fieldCls} h-28`} value={form.terminos} onChange={(e) => setForm({ ...form, terminos: e.target.value })}
              placeholder="Ej. El taller no se responsabiliza por datos o cuentas; se recomienda respaldar la información y cerrar sesión antes de entregar el equipo…" />
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><PackageSearch className="w-4 h-4" /> Fase 4: bodegaje, avisos y usados</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Tarifa de bodegaje diaria sin IVA ($)">
            <input type="number" min="0" step="0.01" className={fieldCls} value={formF4.bodegaje_diario} onChange={(e) => setFormF4({ ...formF4, bodegaje_diario: e.target.value })} />
          </Campo>
          <Campo label="Servicio con el que se factura el bodegaje">
            <select className={fieldCls} value={formF4.producto_bodegaje_id} onChange={(e) => setFormF4({ ...formF4, producto_bodegaje_id: e.target.value })}>
              <option value="">— Elegir —</option>
              {data?.servicios.map((s) => <option key={s.id} value={s.id}>{s.codigo} · {s.nombre}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">Sin este servicio configurado, el bodegaje no se cobra al entregar (solo informa los días en la orden).</p>

          <Campo label="Días de los avisos al cliente (separados por coma)">
            <input className={fieldCls} value={formF4.avisos_dias} onChange={(e) => setFormF4({ ...formF4, avisos_dias: e.target.value })} placeholder="30, 60, 90" />
          </Campo>
          <p className="text-xs text-muted-foreground">El último número es el umbral para poder declarar el equipo abandonado (requiere todos los avisos enviados con evidencia).</p>

          <Campo label="Categoría de compras para la liquidación de usados">
            <select className={fieldCls} value={formF4.categoria_usados} onChange={(e) => setFormF4({ ...formF4, categoria_usados: e.target.value })}>
              <option value="">— Elegir —</option>
              {opcF4?.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Campo>
          {opcF4 && opcF4.categorias.length === 0 && (
            <p className="flex items-center gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> No hay categorías de compras: cree una en Financiero antes de comprar un usado.</p>
          )}
          <Campo label={opcF4?.cc_modo === 'contable' ? 'Centro de costo contable de esas compras' : 'Centro de costo de esas compras'}>
            <select className={fieldCls} value={formF4.centro_costo_usados} onChange={(e) => setFormF4({ ...formF4, centro_costo_usados: e.target.value })}>
              <option value="">— Elegir —</option>
              {opcF4?.centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Días de garantía de los usados reacondicionados">
            <input type="number" min="0" className={fieldCls} value={formF4.garantia_usados_dias} onChange={(e) => setFormF4({ ...formF4, garantia_usados_dias: e.target.value })} />
          </Campo>
          <div className="flex justify-end">
            <Button size="sm" isLoading={guardarF4.isPending} onClick={() => guardarF4.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar fase 4</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Tv className="w-4 h-4" /> Pantalla de cola en la TV del local</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Abra este enlace (o cárguelo en el kiosko de cartelería) en la TV del local: muestra la cola de órdenes en vivo con el número de orden y el equipo,
            sin el nombre del cliente (protección de datos).
          </p>
          {tvError && <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Guarde la configuración general primero para obtener el enlace de la TV.</p>}
          {tv?.token && (
            <>
              <div className="flex items-center gap-2">
                <input className={`${fieldCls} font-mono text-xs`} readOnly value={urlTvTaller(tv.token)} onFocus={(e) => e.currentTarget.select()} />
                <Button size="sm" variant="outline" onClick={() => void copiarUrlTv(urlTvTaller(tv.token))}>
                  <Copy className="w-4 h-4 mr-1" /> {copiado ? 'Copiado' : 'Copiar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(urlTvTaller(tv.token), '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-1" /> Abrir
                </Button>
              </div>
              <div className="inline-block rounded-lg bg-white p-3">
                <QRCodeSVG value={urlTvTaller(tv.token)} size={140} includeMargin />
              </div>
              <div>
                <Button size="sm" variant="outline" isLoading={regenerarTv.isPending} onClick={async () => {
                  if (!(await dlg.confirmar('¿Regenerar el enlace de la TV?', {
                    detalle: 'El enlace actual dejará de funcionar de inmediato; tendrá que volver a cargar la URL (o el QR) en la pantalla del local.',
                    peligro: true,
                  }))) return;
                  regenerarTv.mutate();
                }}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Regenerar enlace
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {dlg.ui}

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar configuración</Button>
      </div>
    </div>
  );
}
